import React, { useMemo, useEffect, useState } from 'react';
import styles from './index.module.less';
import { Affix, Skeleton } from 'antd';
import { ArticleDetailModel, HomeArticleModel } from '@/types/home';
import { imgUrl } from '@/shared/utils';
import useScreenSize from '@/lib/hooks/utils/useScreenSize';
import { useRouter } from 'next/router';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { CommonBaseContent } from '@/components/Layouts/CommonBackContent';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { AppSettings } from '@/shared/app-common';
import dayjs from 'dayjs';

const BookItem = dynamic(() => import('../BookItem'), { ssr: true });
const SharePage = dynamic(() => import('@/components/Common/Share'), { ssr: true });
const RightSideContent = dynamic(() => import('../RightSideContent'), { ssr: true });
const ArticleVideoContent = dynamic(() => import('../ArticleVideoContent'), { ssr: false });
// const IntroductionContent = dynamic(() => import('../IntroductionContent'), { ssr: true });
const BodyContent = dynamic(() => import('../BodyContent'), { ssr: true });
const ArticleListContent = dynamic(() => import('../ArticleListContent'), { ssr: false });
const BottomFlexButton = dynamic(() => import('../BottomFlexButton'), { ssr: false });

const ArticleDetailUI = ({
  model,
  contentData,
  recommendTags,
}: {
  model?: ArticleDetailModel;
  contentData?: any;
  recommendTags?: [HomeArticleModel];
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { width } = useScreenSize();
  const currentHref = useMemo(
    () => (typeof window !== 'undefined' ? window.location.href : ''),
    [],
  );
  // const { onShowArticleBottomAlert, isMobile } = useConfigProvider();

  const [isManualScroll, setIsManualScroll] = useState(false);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      setIsManualScroll(true);
    };

    const handleTouchMove = (event: TouchEvent) => {
      setIsManualScroll(true);
    };

    window.addEventListener('wheel', handleWheel);
    window.addEventListener('touchmove', handleTouchMove);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  const LoadingSkeleton = useMemo(() => {
    return (
      <>
        <Skeleton active />
        <Skeleton.Image active style={{ marginTop: '20px', width: '100%', aspectRatio: '16/9' }} />
        <Skeleton active style={{ marginTop: '20px' }} />
      </>
    );
  }, []);

  const [processedContent, setProcessedContent] = useState({ updatedHtml: '', h2Contents: [] });

  useEffect(() => {
    if (typeof window !== 'undefined' && contentData?.body) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(contentData.body, 'text/html');

      const h2Elements = doc.querySelectorAll('h2');
      const h2Contents: string[] = [];

      h2Elements.forEach((h2, index) => {
        h2Contents.push(h2.textContent || '');
        h2.id = `${index + 1}`;
        h2.style.scrollMarginTop = '80px';
      });

      const imgElements = doc.querySelectorAll('img');
      imgElements?.forEach((img) => {
        if (!img.hasAttribute('loading')) {
          img.setAttribute('loading', 'lazy');
        }

        const originalSrc = img.getAttribute('src');
        const resizeType = width > 576 ? 'big' : 'mid';
        if (originalSrc) {
          img.setAttribute('src', imgUrl(originalSrc, resizeType));
        }
      });

      const updatedHtml = doc.body.innerHTML;
      setProcessedContent({ updatedHtml, h2Contents });
    }
  }, [contentData?.body, width]);

  const getMidShareContent = useMemo(() => {
    return <>{width <= 1150 && <SharePage shareUrl={currentHref} />}</>;
  }, [width, currentHref]);

  const getTopShareContent = useMemo(() => {
    return (
      <>
        {width > 1150 && (
          <div style={{ float: 'left', marginLeft: '-67px' }}>
            <Affix offsetTop={74}>
              <SharePage shareUrl={currentHref} />
            </Affix>
          </div>
        )}
      </>
    );
  }, [width, currentHref]);

  const getRecommendTagsContent = useMemo(() => {
    return (
      <>
        {recommendTags?.length > 0 && (
          <div className={styles.recommendTags}>
            <h2>{t('Read More')}</h2>
            <div className={styles.tagsContainer}>
              {(recommendTags ?? []).map((item: HomeArticleModel, index: number) => {
                return (
                  <div className={styles.linkContainer} key={item?.article_id}>
                    <span>{index + 1}.</span>
                    <Link
                      className={styles.link}
                      href={`/instagram-tracking-blog/${item?.title_id}`}
                      // href={`/instagram-tracking-blog/${item?.article_id}/${getPathTitle(
                      //   item?.title,
                      // )}`}
                    >
                      {item.title}
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </>
    );
  }, [recommendTags]);

  const getArticleTopContent = useMemo(() => {
    return (
      <div className={styles.articleTopWrapper}>
        <div className={styles.time}>{dayjs(model?.create_time).format('YYYY-MM')}</div>
        {contentData?.book && <BookItem contentData={contentData} />}

        <h1 className={styles.title}>{contentData?.title ?? ' '}</h1>

        {model?.article_landings?.length > 0 && (
          <div className={styles.pageTagList}>
            {model?.article_landings?.map((item, index) => {
              return (
                <span
                  key={item?.name}
                  onClick={() => {
                    const tempPath = item?.url_path?.startsWith('http')
                      ? item?.url_path
                      : AppSettings.webHost + item?.url_path;
                    window.open(tempPath, '_blank');
                  }}
                >
                  {item?.name}
                </span>
              );
            })}
          </div>
        )}

        {contentData?.image_url && (
          <Image
            className={styles.image}
            src={imgUrl(contentData?.image_url, width > 576 ? 'big' : 'mid')}
            loading="lazy"
            alt={contentData?.image_text}
            layout="responsive"
            width={300}
            height={300}
          />
        )}
      </div>
    );
  }, [model?.create_time, model?.title, model?.article_landings, contentData, width]);

  return (
    <CommonBaseContent style={{ maxWidth: '1040px', minHeight: '100vh' }}>
      {getTopShareContent}

      <div className={styles.homeContent}>
        <div className={styles.leftContent}>
          <div className={styles.leftContainer}>
            <div className={styles.topSubCategory}>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`${AppSettings.webHost}`);
                }}
              >
                {t('Home')}
              </span>
              <span>{`>`}</span>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`/instagram-tracking-blog`);
                }}
              >
                {t('Blog')}
              </span>
              <span>{`>`}</span>
              <span
                className={styles.link}
                onClick={() => {
                  router.push(`/instagram-tracking-blog?classifies=${model?.type}`);
                }}
              >
                {model?.type}
              </span>
              <span>{`>`}</span>
              <span>{t('Article Details')}</span>
            </div>

            {contentData == undefined || contentData?.title == undefined ? (
              <>{LoadingSkeleton}</>
            ) : (
              <>
                {getArticleTopContent}
                {/* <IntroductionContent introduction={contentData?.introduction} /> */}

                <div className={styles.articleTable}>
                  <span>{t('Table of Content')}</span>
                  <ul>
                    {processedContent.h2Contents.map((item: string, index: number) => (
                      <li key={index}>
                        <Link
                          className={styles.liLink}
                          href={`/instagram-tracking-blog/${model?.title_id}#${index + 1}`}
                        >
                          {item}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <BodyContent body={processedContent.updatedHtml} />
                <ArticleVideoContent body={contentData?.body} />
                {getRecommendTagsContent}
                {getMidShareContent}
              </>
            )}
          </div>

          <ArticleListContent id={model?.article_id} />
        </div>

        <RightSideContent />
      </div>

      {isManualScroll && model?.article_button && (
        <BottomFlexButton model={model?.article_button} />
      )}
    </CommonBaseContent>
  );
};

export default ArticleDetailUI;
